import React, { useState } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { BookOpen, Play, FileText, CheckCircle, Clock, Award, Video, Download } from 'lucide-react';

const Schulung = () => {
  const [completedModules, setCompletedModules] = useState([]);

  const toggleModuleCompletion = (moduleId) => {
    if (completedModules.includes(moduleId)) {
      setCompletedModules(completedModules.filter(id => id !== moduleId));
    } else {
      setCompletedModules([...completedModules, moduleId]);
    }
  };

  const schulungsModule = [
    {
      id: 1,
      title: 'Erste Schritte',
      description: 'Einführung in die Autogutachter24 Software',
      duration: '15 Min',
      type: 'video',
      lessons: [
        'Dashboard Übersicht',
        'Navigation und Menüs',
        'Benutzereinstellungen'
      ]
    },
    {
      id: 2,
      title: 'Akten erstellen und verwalten',
      description: 'Lernen Sie, wie Sie neue Akten anlegen und bearbeiten',
      duration: '25 Min',
      type: 'video',
      lessons: [
        'Neue Akte anlegen',
        'Fahrzeugscheinscanner nutzen',
        'Arbeitsschritte und Status',
        'Gutachten erstellen'
      ]
    },
    {
      id: 3,
      title: 'Kundenverwaltung',
      description: 'Effiziente Verwaltung Ihrer Kundendaten',
      duration: '20 Min',
      type: 'video',
      lessons: [
        'Kunden anlegen',
        'Kundendaten importieren (XML/CSV/JSON)',
        'Kundensuche und Filter'
      ]
    },
    {
      id: 4,
      title: 'Fahrzeugscheinscanner',
      description: 'Automatische Datenextraktion aus Fahrzeugscheinen',
      duration: '15 Min',
      type: 'video',
      lessons: [
        'Scanner-Funktion nutzen',
        'Unterstützte Formate',
        'Datenvalidierung'
      ]
    },
    {
      id: 5,
      title: 'Terminverwaltung',
      description: 'Termine planen und koordinieren',
      duration: '10 Min',
      type: 'video',
      lessons: [
        'Termine erstellen',
        'Kalenderansicht',
        'Erinnerungen einrichten'
      ]
    },
    {
      id: 6,
      title: 'Statistiken und Berichte',
      description: 'Auswertungen und Analysen erstellen',
      duration: '18 Min',
      type: 'video',
      lessons: [
        'Dashboard-Statistiken',
        'Berichte generieren',
        'Export-Funktionen'
      ]
    }
  ];

  const dokumentation = [
    {
      title: 'Benutzerhandbuch',
      description: 'Vollständige Dokumentation aller Funktionen',
      icon: BookOpen,
      size: '2.5 MB'
    },
    {
      title: 'Quick-Start Guide',
      description: 'Schnelleinstieg in 10 Schritten',
      icon: FileText,
      size: '850 KB'
    },
    {
      title: 'FAQ Sammlung',
      description: 'Häufig gestellte Fragen und Antworten',
      icon: FileText,
      size: '1.2 MB'
    }
  ];

  const completionPercentage = Math.round((completedModules.length / schulungsModule.length) * 100);

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Schulung & Training</h1>
          <p className="text-gray-500 mt-1">Lernen Sie die Software optimal zu nutzen</p>
        </div>

        {/* Fortschritt */}
        <Card className="border-0 shadow-lg bg-gradient-to-br from-blue-50 to-indigo-50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="p-3 bg-blue-600 rounded-lg">
                  <Award className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Ihr Fortschritt</h3>
                  <p className="text-sm text-gray-600">
                    {completedModules.length} von {schulungsModule.length} Modulen abgeschlossen
                  </p>
                </div>
              </div>
              <div className="text-right">
                <div className="text-3xl font-bold text-blue-600">{completionPercentage}%</div>
              </div>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div 
                className="bg-blue-600 h-3 rounded-full transition-all duration-500"
                style={{ width: `${completionPercentage}%` }}
              ></div>
            </div>
          </CardContent>
        </Card>

        {/* Schulungsmodule */}
        <div>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Schulungsmodule</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {schulungsModule.map((modul) => {
              const isCompleted = completedModules.includes(modul.id);
              return (
                <Card key={modul.id} className={`hover:shadow-lg transition-shadow ${isCompleted ? 'border-green-500 border-2' : ''}`}>
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-3">
                        <div className={`p-3 rounded-lg ${isCompleted ? 'bg-green-100' : 'bg-blue-100'}`}>
                          {isCompleted ? (
                            <CheckCircle className="w-6 h-6 text-green-600" />
                          ) : (
                            <Video className="w-6 h-6 text-blue-600" />
                          )}
                        </div>
                        <div className="flex-1">
                          <CardTitle className="text-lg">{modul.title}</CardTitle>
                          <p className="text-sm text-gray-600 mt-1">{modul.description}</p>
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center space-x-4 mb-4 text-sm text-gray-600">
                      <div className="flex items-center space-x-1">
                        <Clock className="w-4 h-4" />
                        <span>{modul.duration}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <FileText className="w-4 h-4" />
                        <span>{modul.lessons.length} Lektionen</span>
                      </div>
                    </div>

                    {/* Lessons List */}
                    <div className="mb-4 space-y-2">
                      {modul.lessons.map((lesson, index) => (
                        <div key={index} className="flex items-center space-x-2 text-sm text-gray-700">
                          <div className={`w-2 h-2 rounded-full ${isCompleted ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                          <span>{lesson}</span>
                        </div>
                      ))}
                    </div>

                    <div className="flex space-x-2">
                      <Button 
                        className="flex-1 bg-blue-600 hover:bg-blue-700"
                        onClick={() => alert('Video-Player wird geöffnet...')}
                      >
                        <Play className="w-4 h-4 mr-2" />
                        {isCompleted ? 'Erneut ansehen' : 'Starten'}
                      </Button>
                      <Button
                        variant={isCompleted ? "outline" : "default"}
                        onClick={() => toggleModuleCompletion(modul.id)}
                        className={isCompleted ? 'border-green-500 text-green-600' : ''}
                      >
                        {isCompleted ? (
                          <CheckCircle className="w-4 h-4" />
                        ) : (
                          'Als erledigt markieren'
                        )}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Dokumentation */}
        <div>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Dokumentation</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {dokumentation.map((doc, index) => {
              const Icon = doc.icon;
              return (
                <Card key={index} className="hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6">
                    <div className="flex flex-col items-center text-center">
                      <div className="p-4 bg-gray-100 rounded-lg mb-4">
                        <Icon className="w-8 h-8 text-gray-600" />
                      </div>
                      <h3 className="font-semibold text-gray-900 mb-2">{doc.title}</h3>
                      <p className="text-sm text-gray-600 mb-4">{doc.description}</p>
                      <div className="text-xs text-gray-500 mb-4">{doc.size}</div>
                      <Button 
                        className="w-full bg-blue-600 hover:bg-blue-700"
                        onClick={() => alert('Download wird gestartet...')}
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Herunterladen
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Hilfe */}
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="pt-6">
            <div className="flex items-start space-x-4">
              <div className="p-3 bg-blue-600 rounded-lg">
                <BookOpen className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-2">Brauchen Sie weitere Hilfe?</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Unser Support-Team und KI-Chat-Assistent stehen Ihnen jederzeit zur Verfügung.
                </p>
                <div className="flex space-x-2">
                  <Button 
                    variant="outline"
                    onClick={() => window.location.href = '/support'}
                  >
                    Support kontaktieren
                  </Button>
                  <Button 
                    className="bg-blue-600 hover:bg-blue-700"
                    onClick={() => window.location.href = '/chat'}
                  >
                    KI-Chat öffnen
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default Schulung;
